import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

const FALLBACK = [
  { author: "Rahul Sharma", route: "Noida Sec 62 → Gurugram Sec 56", rating: 5, text: "Bilkul time pe pahuche, packing bahut acchi thi. Kuch bhi tuta nahi. Highly recommended!" },
  { author: "Priya Verma", route: "Ghaziabad Indirapuram → Noida Sec 78", rating: 5, text: "3 BHK ka saman ek din mein shift kiya. Team professional thi, price bhi reasonable." },
  { author: "Anjali Gupta", route: "Faridabad Sec 21 → Delhi Dwarka", rating: 5, text: "Insurance included tha, hamara TV safe pahuncha. Diwali ke pehle time pe move ho gaya." },
  { author: "Manoj Kumar", route: "Gurugram DLF Phase 2 → Noida Sec 137", rating: 5, text: "Bhut ache log, respectful and careful. Free bubble wrap bhi mila. Would use again." },
  { author: "Suresh Mehra", route: "Delhi Rohini → Ghaziabad Vaishali", rating: 5, text: "Office shifting kiya, sab kuch labeled and organized. Zero downtime for our team." },
  { author: "Neha Singh", route: "Greater Noida Pari Chowk → Lucknow", rating: 5, text: "Intercity move NCR to Lucknow. Truck GPS tracked tha, updates milte rahe. Trustworthy!" },
];

export default function ReviewsSection() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reviews")
      .then((r) => setData(r.data))
      .catch(() => setData({ source: "seeded", stale: true, rating: 4.9, user_rating_count: 2480, reviews: FALLBACK }));
  }, []);

  const rating = data?.rating ?? 4.9;
  const count = data?.user_rating_count ?? 2480;
  const isLive = data?.source === "google" || data?.source === "google-cache";
  const reviews = (data?.reviews && data.reviews.length >= 3) ? data.reviews : FALLBACK;
  const mapsUri = data?.google_maps_uri;

  return (
    <section className="py-16 sm:py-20 bg-slate-50" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-yellow-300 rounded-full px-4 py-1 shadow-sm">
            <div className="flex text-yellow-500">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-current" />)}</div>
            <span className="font-bold text-[#0F172A]">{Number(rating).toFixed(1)} / 5.0</span>
            <span className="text-sm text-slate-500">• {count.toLocaleString("en-IN")}+ Reviews</span>
            {isLive ? (
              <Badge className="bg-[#22C55E] text-white text-[10px] ml-2">Live from Google</Badge>
            ) : null}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-4 text-[#0F172A]">Real Families. Real Reviews.</h2>
          <p className="text-slate-600 mt-2">Delhi NCR ke logon ne humpe bharosa kyun kiya — unhi ki zubani.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
          {reviews.slice(0, 6).map((r, i) => (
            <Card key={i} className="p-6 bg-white hover:shadow-lg transition-shadow border border-slate-200">
              <div className="flex text-yellow-500 mb-2">
                {Array.from({length: r.rating || 5}).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="text-slate-700 text-sm leading-relaxed">"{r.text}"</p>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-[#0F172A]">{r.author}</div>
                  <div className="text-xs text-slate-500">{r.route || r.relative_time || "Verified customer"}</div>
                </div>
                {r.google_maps_uri && (
                  <a href={r.google_maps_uri} target="_blank" rel="noreferrer noopener" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5 flex-none">
                    Google <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
        {mapsUri && (
          <div className="text-center mt-8">
            <a href={mapsUri} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] hover:text-[#F97316]" data-testid="reviews-google-link">
              See all reviews on Google Maps <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
